import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  formatNumber,
  truncateToTwoDecimals,
} from "../../../../../utility/helper";
import { wealthAssetTypeOption, sectorss } from "../../../../../utility/data";
import moment from "moment";

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const { roi, date } = payload[0].payload;
    const roiPercentage = truncateToTwoDecimals(roi);

    return (
      <div
        style={{
          padding: "10px",
          color: "#333",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
        }}
      >
        <div className="flex gap-2">
          <strong style={{ margin: 0, color: "#333" }}>ROI:</strong>
          <p style={{ color: "#333" }}>{roiPercentage}%</p>
        </div>
        <div className="flex gap-2">
          <strong style={{ margin: 0, color: "#333" }}>Date:</strong>
          <p style={{ color: "#333" }}>{date}</p>
        </div>
      </div>
    );
  }
  return null;
};

const RechartExample = ({ graphData, fortuneData, groupsData }) => {
  const [activeIndex, setActiveIndex] = useState({});
  const [chartsData, setChartsData] = useState([]);

  useEffect(() => {
    if (fortuneData) {
      const getMappedName = (list, key) =>
        list?.find((item) => item.value === key)?.name || key;
      setChartsData([
        {
          title: "Sector",
          data:
            fortuneData.pie_sector_data?.map((item) => ({
              name: item.sector,
              value: parseFloat(item.percentage?.toFixed(2)),
              displayName: getMappedName(sectorss, item.sector),
            })) || [],
          colors: [
            "#FF85B6",
            "#57FFFF",
            "#FFBF00",
            "#007BFF",
            "#1ABC9C",
            "#FF4500",
            "#32CD32",
            "#8A2BE2",
            "#FFD700",
            "#DC143C",
            "rgb(133, 220, 20)",
            "rgb(220, 20, 170)",
          ],
        },
        {
          title: "Structure",
          data:
            fortuneData.pie_structure_data?.map((item) => ({
              name: `Group ${item.group}`,
              value: parseFloat(item.percentage?.toFixed(2)),
              displayName: getMappedName(
                groupsData?.map((g) => ({
                  name: g.name,
                  value: `Group ${g.id}`,
                })),
                `Group ${item.group}`
              ),
            })) || [],
          colors: [
            "#FFBF00",
            "#FF85B6",
            "#57FFFF",
            "#32CD32",
            "#8A2BE2",
            "#FF4500",
          ],
        },
        {
          title: "Allocation",
          data:
            fortuneData.pie_allocation_data?.map((item) => ({
              name: item.asset_class,
              value: parseFloat(item.percentage?.toFixed(2)),
              displayName: getMappedName(
                wealthAssetTypeOption,
                item.asset_class
              ),
            })) || [],
          colors: ["#FF85B6", "#57FFFF", "#FFBF00", "#007BFF", "#1ABC9C"],
        },
      ]);
    }
  }, [fortuneData, groupsData]);

  const handleHover = (chartIndex, index) => {
    setActiveIndex((prev) => {
      const newState = { ...prev };
      if (typeof index === "number") {
        newState[chartIndex] = index;
      } else {
        delete newState[chartIndex];
      }
      return newState;
    });
  };

  return (
    <div className="flex gap-4">
      <div className="w-full">
        <div className="md:w-2/3 w-full overflow-x-auto md:ml-14 my-10">
          <table className="min-w-[670px] text-md border-collapse text-white">
            <thead>
              <tr>
                <th className="text-left pr-4"></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="pb-2">Your fortune is now valued a</td>
                <td
                  className={`pb-2 ${
                    parseFloat(fortuneData?.fortune ?? 0) < 0
                      ? "text-expense dark:text-warning"
                      : "text-secondary dark:text-primary-gradient"
                  }`}
                >
                  {formatNumber(
                    truncateToTwoDecimals(fortuneData?.fortune ?? "0")
                  )}{" "}
                  CHF
                </td>
              </tr>
              <tr>
                <td className="pb-2">
                  The fortune has{" "}
                  {parseFloat(fortuneData?.fortune_increased ?? 0) < 0
                    ? "decreased"
                    : "increased"}{" "}
                  by
                </td>
                <td
                  className={`pb-2 ${
                    parseFloat(fortuneData?.fortune_increased ?? 0) < 0
                      ? "text-expense dark:text-warning"
                      : "text-secondary dark:text-primary-gradient"
                  }`}
                >
                  {formatNumber(
                    truncateToTwoDecimals(fortuneData?.fortune_increased ?? "0")
                  )}{" "}
                  CHF
                </td>
                <td className="pb-2">Year to date</td>
              </tr>
              <tr>
                <td className="">
                  Due to total{" "}
                  {parseFloat(fortuneData?.total_profit ?? 0) < 0
                    ? "loss"
                    : "profit"}{" "}
                  of
                </td>
                <td
                  className={`${
                    parseFloat(fortuneData?.total_profit ?? 0) < 0
                      ? "text-expense dark:text-warning"
                      : "text-secondary dark:text-primary-gradient"
                  }`}
                >
                  {formatNumber(
                    truncateToTwoDecimals(fortuneData?.total_profit ?? "0")
                  )}{" "}
                  CHF
                </td>
                <td
                  className={`text-center ${
                    parseFloat(fortuneData?.percentage_value ?? 0) < 0
                      ? "text-expense dark:text-warning"
                      : "text-secondary dark:text-primary-gradient"
                  }`}
                >
                  {formatNumber(
                    truncateToTwoDecimals(fortuneData?.percentage_value ?? 0)
                  )}{" "}
                  %
                </td>

                <td className="">and a net flow of</td>
                <td
                  className={`${
                    parseFloat(fortuneData?.net_flow ?? 0) < 0
                      ? "text-expense dark:text-warning"
                      : "text-secondary dark:text-primary-gradient"
                  }`}
                >
                  {formatNumber(
                    truncateToTwoDecimals(fortuneData?.net_flow ?? "0")
                  )}{" "}
                  CHF
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <ResponsiveContainer height={450}>
          <LineChart
            data={fortuneData?.graph_data?.map((item) => ({
              ...item,
              roi: item.roi ?? 0,
            }))}
          >
            <defs>
              <linearGradient id="colorGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#00BFFF" />
                <stop offset="100%" stopColor="#00BFFF" />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="0 1" stroke="#ccc" />
            <XAxis
              dataKey="date"
              axisLine={false}
              tickFormatter={(value) => value && moment(value).format("DD MMM")}
              tick={{ fill: "white", fontSize: 12 }}
              tickLine={false}
              angle={-30}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              minTickGap={10}
              tick={{ fill: "white", fontSize: 12 }}
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip content={<CustomTooltip />} cursor={false} />
            <Line
              type="linear"
              dataKey="roi"
              stroke="url(#colorGradient)"
              dot={false}
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
        <div className="flex md:justify-center items-center text-center text-info dark:text-white md:flex-row flex-col md:gap-28 gap-4 mt-12">
          <div className="flex flex-col gap-1">
            <span className="">Unrealized profit</span>
            <span
              className={`${
                parseFloat(fortuneData?.unrealized ?? 0) < 0
                  ? "text-expense dark:text-warning"
                  : "text-secondary dark:text-primary-gradient"
              }`}
            >
              {fortuneData?.unrealized
                ? formatNumber(truncateToTwoDecimals(fortuneData?.unrealized))
                : "0"}{" "}
              CHF
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="">Sale</span>
            <span className="text-secondary dark:text-primary-gradient">
              {fortuneData?.realized
                ? formatNumber(truncateToTwoDecimals(fortuneData?.realized))
                : "0"}{" "}
              CHF
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="">Income</span>
            <span className="text-secondary dark:text-primary-gradient">
              {fortuneData?.income ? formatNumber(truncateToTwoDecimals(fortuneData?.income)) : "0"}{" "}
              CHF
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="">Expense</span>
            <span className="text-expense dark:text-warning">
              CHF{" "}
              {fortuneData?.expense ? formatNumber(truncateToTwoDecimals(fortuneData?.expense)) : "0"}{" "}
              CHF
            </span>
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:justify-around gap-10 mt-16">
          {chartsData.map((chart, chartIndex) => (
            <div key={chartIndex} className="text-center w-full md:w-1/3">
              <h3 className="text-secondary dark:text-white font-bold mb-2">
                {chart.title}
              </h3>
              <ResponsiveContainer height={200}>
                <PieChart>
                  <Tooltip
                    cursor={{ fill: "none" }}
                    content={({ payload }) => {
                      if (!payload || payload.length === 0) return null;
                      const data = payload[0]?.payload;
                      return (
                        <div className="bg-white text-primary p-2 rounded shadow-md text-sm">
                          <strong>
                            {data?.displayName || data?.name}: {data?.value}%
                          </strong>
                        </div>
                      );
                    }}
                  />
                  <Pie
                    data={chart.data}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                    activeShape={{ outerRadius: 80 }}
                    onMouseEnter={(_, index) => handleHover(chartIndex, index)}
                    onMouseLeave={() => handleHover(chartIndex)}
                    activeIndex={
                      activeIndex[chartIndex] !== undefined
                        ? activeIndex[chartIndex]
                        : null
                    }
                  >
                    {chart.data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={chart.colors[index]} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="flex gap-3 justify-center flex-wrap">
                {chart.data.map((entry, index) => {
                  return (
                    <div
                      key={index}
                      className="md:flex gap-2 items-center mb-2"
                    >
                      <span
                        className="hidden md:block"
                        style={{
                          width: 12,
                          height: 12,
                          backgroundColor: chart.colors[index],
                          borderRadius: 2,
                        }}
                      ></span>
                      <span className="text-lightPrimary dark:text-white text-sm">
                        <span className="text-lightPrimary dark:text-white text-sm">
                          {entry.displayName || entry.name}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RechartExample;
